/**
 * Portable Workspace Import — super-admin modal.
 *
 * Two-step UX:
 *   1. User drops a `workspace_*.zip` (exported from another FlowUp).
 *      We POST it to /api/admin/workspace/check and show a compatibility
 *      report (modules, routes, collections, counts, aliases).
 *   2. If the report is green, we offer "בצע ייבוא מלא". POST same ZIP
 *      to /api/admin/workspace/import — collections are wiped and
 *      rewritten, uploads land in /api/storage/*, devices come back
 *      `disconnected` ready for QR re-pairing.
 *
 * If the check returns `ok: false`, the import button is disabled and we
 * render a clear "Missing Components" report. NO partial restore — ever.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Upload, Loader2, FileArchive, CheckCircle2, XCircle, AlertTriangle,
  ArrowRight, Wifi, WifiOff, Box, ListChecks, Sparkles,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { api } from "../lib/api";

const fmtBytes = (n) => {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(1)} ${u[i]}`;
};

export function WorkspaceImportModal({ open, onOpenChange, onImported }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [checking, setChecking] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportPreview, setExportPreview] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);  // null | "checking" | configured/missing object
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  // Inline credentials form — persisted ONLY in the SA's browser
  // (localStorage), never sent to any third party. Empty by default.
  const LS_KEY = "flowup_workspace_sync_creds_v1";
  const [creds, setCreds] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      return raw ? JSON.parse(raw) : { base_url: "https://flow-up.co.il", email: "", password: "" };
    } catch {
      return { base_url: "https://flow-up.co.il", email: "", password: "" };
    }
  });
  const [rememberCreds, setRememberCreds] = useState(() => !!localStorage.getItem(LS_KEY));
  const [showPassword, setShowPassword] = useState(false);
  const [report, setReport] = useState(null);    // /check response
  const [result, setResult] = useState(null);    // /import response
  const [dragOver, setDragOver] = useState(false);

  const reset = () => {
    setFile(null);
    setReport(null);
    setResult(null);
    setExportPreview(null);
    setSyncResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  // Refresh PROD sync configuration whenever the modal opens.
  useEffect(() => {
    if (open) {
      refreshSyncConfig();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Manifest-only preview — shows collection counts + upload size BEFORE
   *  building the full ZIP, so the SA can confirm what's inside. */
  const previewExport = async () => {
    try {
      const { data } = await api.get("/admin/workspace/preview");
      setExportPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בטעינת מקדימה");
    }
  };

  /** Streams the ZIP from the backend and triggers a browser download.
   *  Uses native fetch (not axios) so we can grab the binary cleanly. */
  const downloadExport = async () => {
    setExporting(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("flowup_token") || localStorage.getItem("token");
      const resp = await fetch(`${base}/api/admin/workspace/create`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      // Extract filename from Content-Disposition (server provides it).
      const disp = resp.headers.get("Content-Disposition") || "";
      const m = disp.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `workspace_${Date.now()}.zip`;
      const blob = await resp.blob();
      // Trigger browser download.
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("ה-Workspace ירד למחשב שלך", { description: filename });
    } catch (e) {
      toast.error("שגיאה ביצירת ה-ZIP", { description: String(e.message || e) });
    } finally {
      setExporting(false);
    }
  };

  /** Check whether PROD credentials are configured server-side. The
   *  modal calls this on open so the "Sync now" button shows the right
   *  state (enabled/disabled + tooltip). */
  const refreshSyncConfig = async () => {
    setSyncStatus("checking");
    try {
      const { data } = await api.get("/admin/workspace/sync-config");
      setSyncStatus(data);
    } catch {
      setSyncStatus({ configured: false, missing: [], error: true });
    }
  };

  /** One-click PUSH to Production — builds ZIP server-side AND uploads
   *  to PROD's /import endpoint in a single backend call. Sends inline
   *  credentials in the request body so the SA doesn't need to wrangle
   *  Emergent's env-var UI. */
  const syncNow = async () => {
    if (!creds.base_url || !creds.email || !creds.password) {
      toast.error("מלא/י את שלושת השדות (כתובת, מייל, סיסמה)");
      return;
    }
    if (!window.confirm(`לסנכרן עכשיו את Workspace ל-${creds.base_url}?\nזה ידחוף את כל הנתונים מ-Preview ליעד.`)) {
      return;
    }
    // Persist / clear creds in localStorage based on the "remember" toggle.
    if (rememberCreds) {
      localStorage.setItem(LS_KEY, JSON.stringify(creds));
    } else {
      localStorage.removeItem(LS_KEY);
    }
    setSyncing(true);
    setSyncResult(null);
    try {
      const { data } = await api.post("/admin/workspace/push-to-production", creds);
      setSyncResult(data);
      if (data.ok) {
        toast.success("Production סונכרן בהצלחה", {
          description: `${data.filename} · ${fmtBytes(data.zip_bytes)}`,
        });
        onImported?.();
      } else if (data.status === 413) {
        // CRITICAL — nginx on the production target rejects the upload
        // because the ZIP exceeds `client_max_body_size`. Tell the user
        // EXACTLY this rather than a generic "sync failed", and give
        // them an actionable next step.
        toast.error(`⛔ הקובץ גדול מדי לשרת — ${fmtBytes(data.zip_bytes || 0)}`, {
          description: "ה-nginx של flow-up.co.il מוגבל ל-10MB לבקשה. צור קשר עם Emergent Support להגדלת client_max_body_size ל-200MB, או הקטן את המדיה לפני סנכרון.",
          duration: 18000,
        });
      } else if (data.status === 401 || data.status === 403) {
        toast.error("אימות נכשל מול שרת ה-Production", {
          description: "וודא ש-PRODUCTION_SA_EMAIL ו-PRODUCTION_SA_PASSWORD נכונים.",
          duration: 12000,
        });
      } else {
        toast.error(data.message || "סנכרון נכשל", {
          description: `${data.error || data.target || ""} (HTTP ${data.status || "?"})`,
          duration: 12000,
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "שגיאה");
    } finally {
      setSyncing(false);
    }
  };
  const close = () => {
    reset();
    onOpenChange(false);
  };

  const pickFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".zip")) {
      toast.error("נא לבחור קובץ .zip של Workspace");
      return;
    }
    setFile(f);
    setReport(null);
    setResult(null);
  };

  const onCheck = async () => {
    if (!file) return;
    setChecking(true);
    setReport(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/workspace/check", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReport(data);
      toast.success(data.report?.ok ? "ה-Workspace תואם — מוכן לייבוא" : "נמצאו אי-התאמות — ראה דוח");
    } catch (e) {
      toast.error(e.response?.data?.detail || "בדיקה נכשלה");
    } finally {
      setChecking(false);
    }
  };

  const onImport = async () => {
    if (!file || !report?.report?.ok) return;
    if (!window.confirm(
      "פעולת ייבוא מלא תמחק את כל הנתונים הקיימים בקולקציות המופיעות בדוח " +
      "ותכתוב מחדש מה-ZIP. ההתקנים יוחזרו ל-disconnected ויידרשו לסרוק QR מחדש.\n\n" +
      "להמשיך?"
    )) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/workspace/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,  // 10 min — large workspaces have hundreds of MB of media
      });
      setResult(data);
      toast.success(`ייבוא הושלם — ${Object.values(data.applied?.applied_collections || {}).reduce((a, b) => a + b, 0)} מסמכים שוחזרו`);
      if (onImported) onImported(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "ייבוא נכשל");
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  const rep = report?.report;
  const m = report?.manifest;
  const isGreen = rep?.ok === true;

  return (
    <Dialog open={open} onOpenChange={(v) => v ? onOpenChange(v) : close()}>
      <DialogContent
        className="max-w-3xl bg-white text-stone-900 max-h-[92vh] overflow-y-auto"
        dir="rtl"
        data-testid="workspace-import-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-extrabold">
            <FileArchive className="w-6 h-6 text-indigo-600" />
            ייצוא / ייבוא Workspace
          </DialogTitle>
        </DialogHeader>

        {/* ========== ⭐ ONE-CLICK SYNC TO PRODUCTION ==========
            One button. Uses defaults baked in (URL + email) and prompts
            for password only when needed. After first success, password
            is remembered in localStorage so future clicks are truly
            one-click. */}
        {!result && (
          <div className="rounded-xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 p-5 mb-4 shadow-md shadow-amber-200/40" data-testid="workspace-sync-section">
            <Button
              onClick={async () => {
                // True 1-click. No prompt. The backend tries dev-bypass
                // first (Production has DEV_AUTH_BYPASS=1), then falls
                // back to env-stored credentials. If both fail, we
                // surface a clear error toast — never block with a
                // browser prompt that interrupts the flow.
                setSyncing(true);
                setSyncResult(null);
                try {
                  const { data } = await api.post("/admin/workspace/push-to-production", {});
                  setSyncResult(data);
                  if (data.ok) {
                    toast.success("✓ הסנכרון הצליח", {
                      description: `${data.filename} · ${fmtBytes(data.zip_bytes)}`,
                    });
                    onImported?.();
                  } else if (data.status === 413) {
                    toast.error(`⛔ הקובץ גדול מדי לשרת — ${fmtBytes(data.zip_bytes || 0)}`, {
                      description: "ה-nginx של flow-up.co.il מוגבל ל-10MB לבקשה. צור קשר עם Emergent Support להגדלת client_max_body_size ל-200MB.",
                      duration: 18000,
                    });
                  } else {
                    toast.error(data.message || "סנכרון נכשל", {
                      description: `${data.error || ""} (HTTP ${data.status || "?"})`,
                      duration: 12000,
                    });
                  }
                } catch (e) {
                  toast.error(e.response?.data?.detail || e.message || "שגיאה");
                } finally {
                  setSyncing(false);
                }
              }}
              disabled={syncing}
              className="w-full bg-gradient-to-l from-amber-500 via-orange-500 to-rose-500 hover:opacity-90 text-white font-extrabold h-14 text-base shadow-lg"
              data-testid="workspace-sync-now-btn"
            >
              {syncing
                ? (<><Loader2 className="w-5 h-5 animate-spin" /> מסנכרן... זה לוקח כדקה</>)
                : (<><Sparkles className="w-5 h-5" /> ⚡ סנכרן הכל ל-Production</>)}
            </Button>
            <p className="text-[11px] text-stone-600 mt-2 text-center">
              דחיפת חברות + הפקות + מדיה ל-{creds.base_url || "https://flow-up.co.il"}
            </p>

            {syncResult && (
              <div className={`mt-3 rounded-lg border-2 p-3 text-xs ${
                syncResult.ok
                  ? "bg-emerald-50 border-emerald-300 text-emerald-900"
                  : "bg-rose-50 border-rose-300 text-rose-900"
              }`} data-testid="workspace-sync-result">
                {syncResult.ok ? (
                  <>
                    <div className="font-extrabold mb-1">✓ הסנכרון הצליח</div>
                    <div>{syncResult.filename} · {fmtBytes(syncResult.zip_bytes)}</div>
                  </>
                ) : (
                  <>
                    <div className="font-extrabold mb-1">✗ {syncResult.message || syncResult.error}</div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {!result && (
          <>
            {/* Drop zone */}
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                pickFile(e.dataTransfer.files?.[0]);
              }}
              className={`w-full rounded-xl border-2 border-dashed p-8 text-center transition ${
                dragOver
                  ? "border-indigo-500 bg-indigo-50"
                  : file
                    ? "border-emerald-400 bg-emerald-50"
                    : "border-stone-300 bg-stone-50 hover:border-indigo-400 hover:bg-indigo-50"
              }`}
              data-testid="workspace-dropzone"
            >
              <input
                ref={fileRef}
                type="file"
                accept=".zip"
                className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0])}
                data-testid="workspace-file-input"
              />
              {file ? (
                <>
                  <FileArchive className="w-10 h-10 mx-auto mb-2 text-emerald-600" />
                  <div className="font-bold">{file.name}</div>
                  <div className="text-xs text-stone-500 mt-1">{fmtBytes(file.size)}</div>
                </>
              ) : (
                <>
                  <Upload className="w-10 h-10 mx-auto mb-2 text-stone-400" />
                  <div className="font-bold">גרור לכאן את קובץ ה-ZIP של ה-Workspace</div>
                  <div className="text-xs text-stone-500 mt-1">או לחץ לבחירה — workspace_*.zip מ-FlowUp אחר</div>
                </>
              )}
            </button>

            <div className="flex justify-between gap-2 pt-3 border-t border-stone-200">
              <Button variant="outline" onClick={close} data-testid="workspace-cancel-btn">ביטול</Button>
              <div className="flex gap-2">
                {file && !rep && (
                  <Button
                    onClick={onCheck}
                    disabled={checking}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
                    data-testid="workspace-check-btn"
                  >
                    {checking ? <Loader2 className="w-4 h-4 animate-spin ml-1" /> : <ListChecks className="w-4 h-4 ml-1" />}
                    בדוק תאימות
                  </Button>
                )}
                {rep && (
                  <Button
                    onClick={onImport}
                    disabled={!isGreen || importing}
                    className={`font-bold text-white ${isGreen ? "bg-emerald-600 hover:bg-emerald-700" : "bg-stone-400 cursor-not-allowed"}`}
                    data-testid="workspace-import-btn"
                  >
                    {importing
                      ? <Loader2 className="w-4 h-4 animate-spin ml-1" />
                      : <ArrowRight className="w-4 h-4 ml-1" />}
                    בצע ייבוא מלא
                  </Button>
                )}
              </div>
            </div>

            {/* Compatibility report */}
            {rep && (
              <div
                className={`rounded-xl border p-4 space-y-3 ${
                  isGreen ? "bg-emerald-50 border-emerald-300" : "bg-rose-50 border-rose-300"
                }`}
                data-testid="workspace-compat-report"
              >
                <div className="flex items-center gap-2 font-bold text-lg">
                  {isGreen ? (
                    <><CheckCircle2 className="w-5 h-5 text-emerald-600" /> תואם — מוכן לייבוא</>
                  ) : (
                    <><XCircle className="w-5 h-5 text-rose-600" /> חוסרים — לא ניתן לייבא</>
                  )}
                </div>

                {/* Manifest summary */}
                {m && (
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <Field label="נוצר ב-" value={m.createdAt?.slice(0, 19).replace("T", " ")} />
                    <Field label="מקור (DB)" value={m.source?.dbName || "—"} />
                    <Field label="מסמכים" value={m.contents?.totalDocuments?.toLocaleString() || "—"} />
                    <Field label="קבצי מדיה" value={`${m.contents?.uploadCount || 0} (${fmtBytes(m.contents?.uploadBytes || 0)})`} />
                  </div>
                )}

                {/* Missing components */}
                {!isGreen && (
                  <div className="bg-rose-100 border border-rose-300 rounded-lg p-3 text-sm">
                    <div className="font-bold text-rose-800 mb-2 flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" /> רכיבים חסרים
                    </div>
                    {(rep.missing_modules || []).length > 0 && (
                      <p className="mb-1"><b>Modules:</b> {rep.missing_modules.join(", ")}</p>
                    )}
                    {(rep.missing_route_prefixes || []).length > 0 && (
                      <p className="mb-1"><b>Routes:</b> {rep.missing_route_prefixes.join(", ")}</p>
                    )}
                  </div>
                )}

                {/* Aliases — informational */}
                {(rep.aliased_route_prefixes || []).length > 0 && (
                  <details className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs">
                    <summary className="cursor-pointer font-bold text-amber-800">
                      {rep.aliased_route_prefixes.length} מסלולים מוגשים על-ידי גרסה חדשה — תואם
                    </summary>
                    <ul className="mt-2 space-y-0.5">
                      {rep.aliased_route_prefixes.map((a, i) => (
                        <li key={i}><code>{a.required}</code> → <code>{a.served_by}</code></li>
                      ))}
                    </ul>
                  </details>
                )}

                {/* Collection counts grid */}
                <details className="bg-white/60 rounded-lg p-2 text-xs">
                  <summary className="cursor-pointer font-bold flex items-center gap-1">
                    <Box className="w-3.5 h-3.5" /> פירוט קולקציות ({Object.keys(rep.summary_counts || {}).length})
                  </summary>
                  <div className="grid grid-cols-3 gap-x-3 gap-y-1 mt-2">
                    {Object.entries(rep.summary_counts || {}).map(([k, v]) => (
                      <div key={k} className="flex justify-between">
                        <span className="text-stone-600">{k}</span>
                        <span className="font-bold tabular-nums">{v.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </details>

                {/* Exclusions / notes */}
                {(m?.exclusions || []).length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-xs text-blue-900 flex gap-2">
                    <WifiOff className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>
                      <b>לא מיובא (בכוונה):</b> {m.exclusions.join(" • ")}.
                      ההתקנים יוחזרו ל-disconnected ויידרש סריקת QR מחדש.
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Post-import success view */}
        {result && (
          <div className="space-y-4" data-testid="workspace-result">
            <div className="rounded-xl bg-emerald-50 border border-emerald-300 p-4">
              <div className="flex items-center gap-2 font-bold text-lg text-emerald-800">
                <CheckCircle2 className="w-6 h-6" /> ייבוא הושלם בהצלחה
              </div>
              <p className="text-sm text-emerald-700 mt-1">
                Workspace מ-<code>{result.applied?.source_db || "—"}</code> שוחזר במלואו.
                {result.validation?.ok
                  ? " ספירות הקולקציות תואמות במדויק את ה-manifest."
                  : " יש פערי-ספירה — ראה למטה."}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg border border-stone-200 p-3">
                <div className="text-xs text-stone-500 uppercase tracking-wider font-bold">מסמכים</div>
                <div className="text-2xl font-extrabold mt-1">
                  {Object.values(result.applied?.applied_collections || {}).reduce((a, b) => a + (Number(b) || 0), 0).toLocaleString()}
                </div>
              </div>
              <div className="rounded-lg border border-stone-200 p-3">
                <div className="text-xs text-stone-500 uppercase tracking-wider font-bold">קבצי מדיה</div>
                <div className="text-2xl font-extrabold mt-1">
                  {result.applied?.uploads?.copied || 0}
                  <span className="text-xs font-normal text-stone-500 mr-2">
                    ({fmtBytes(result.applied?.uploads?.bytes || 0)})
                  </span>
                </div>
              </div>
            </div>

            {/* Validation diffs */}
            {(result.validation?.diffs || []).length > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs">
                <div className="font-bold text-amber-800 flex items-center gap-1 mb-2">
                  <AlertTriangle className="w-4 h-4" /> פערי ספירה
                </div>
                <ul className="space-y-0.5">
                  {result.validation.diffs.map((d, i) => (
                    <li key={i}>
                      <code>{d.collection}</code>: ציפיתי <b>{d.expected}</b>, התקבל <b>{d.got}</b>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900 flex gap-2">
              <Wifi className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                <b>צעד הבא:</b> פתח את FlowUpSender ולחץ "מכשירים" → לכל מכשיר יש כעת
                סטטוס disconnected. סרוק QR מחדש כדי להתחבר ל-WhatsApp.
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button onClick={close} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid="workspace-done-btn">
                סיום
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }) {
  return (
    <div className="bg-white/70 rounded-lg px-3 py-2">
      <div className="text-[10px] text-stone-500 uppercase tracking-wider font-bold">{label}</div>
      <div className="font-bold text-sm">{value || "—"}</div>
    </div>
  );
}
